/**
 * React hook for authentication
 * Similar to Vue's useAuth composable
 */

'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authService, type User, type LoginCredentials, type SignupData } from '../lib/authService';
import { normalizeAdminRole } from '@/lib/auth-core';
import analyticsService from '../lib/analyticsService';

/**
 * Root user detection — now database-driven via users.role = 'root'.
 * The login response includes role from users table.
 */

function normalizeUserRole(user: User | null): User | null {
  if (!user) return null;
  const normalizedRole = normalizeAdminRole(user.role);
  if (!normalizedRole || normalizedRole === user.role) return user;
  return { ...user, role: normalizedRole };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => {
    // Initialize with current auth state
    if (typeof window !== 'undefined') {
      return normalizeUserRole(authService.getUser());
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize with current auth state
    if (typeof window !== 'undefined') {
      return authService.isAuthenticated();
    }
    return false;
  });
  const previousAuthRef = useRef(isAuthenticated);
  const previousUserRef = useRef<User | null>(user);

  // Listen for authentication state changes
  useEffect(() => {
    const handleAuthStateChange = (data?: unknown) => {
      const wasAuthenticated = previousAuthRef.current;
      const previousUser = previousUserRef.current;
      const authState = data && typeof data === 'object'
        ? data as { isAuthenticated?: boolean; user?: User | null }
        : {};
      const nextIsAuthenticated = authState.isAuthenticated ?? authService.isAuthenticated();
      const rawUser = authState.user ?? (nextIsAuthenticated ? authService.getUser() : null) ?? null;
      const nextUser = normalizeUserRole(rawUser);

      setUser(nextUser);
      setIsAuthenticated(nextIsAuthenticated);
      
      // Start/stop heartbeat based on auth state
      if (nextIsAuthenticated && nextUser) {
        if (nextUser.id !== undefined && nextUser.id !== null) {
          analyticsService.setUserId(String(nextUser.id));
        }

        if (!wasAuthenticated) {
          analyticsService.trackAuthentication('sign_in', {
            userId: nextUser.id,
            role: nextUser.role,
          });
        }

        // Start heartbeat to keep online status updated (every 2 minutes)
        analyticsService.startHeartbeat(120000);
      } else {
        if (wasAuthenticated) {
          analyticsService.trackAuthentication('sign_out', {
            userId: previousUser?.id,
            role: previousUser?.role,
          });
        }

        // Stop heartbeat on logout
        analyticsService.stopHeartbeat();
      }

      previousAuthRef.current = nextIsAuthenticated;
      previousUserRef.current = nextUser;
    };

    authService.addEventListener('authStateChanged', handleAuthStateChange);
    
    // Start heartbeat if already authenticated on mount
    if (typeof window !== 'undefined' && authService.isAuthenticated()) {
      const existingUser = normalizeUserRole(authService.getUser());
      if (existingUser?.id !== undefined && existingUser?.id !== null) {
        analyticsService.setUserId(String(existingUser.id));
      }
      analyticsService.startHeartbeat(120000);
      previousAuthRef.current = true;
      previousUserRef.current = existingUser;
    }

    // Cleanup event listener on unmount
    return () => {
      authService.removeEventListener('authStateChanged', handleAuthStateChange);
      analyticsService.stopHeartbeat();
    };
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      console.log('[useAuth] Login attempt starting...')
      setIsLoading(true);
      setError(null);
      
      console.log('[useAuth] Calling authService.login...')
      const response = await authService.login(credentials);
      console.log('[useAuth] authService.login response:', {
        success: response.success,
        has_user: !!response.user,
        requires_verification: response.requires_verification,
        requires_mfa: response.requires_mfa,
        requires_device_verification: response.requires_device_verification,
        error: response.error
      })
      
      // Handle multi-step auth flows (success but no final user/tokens yet)
      if (response.success && (response.requires_verification || response.requires_mfa || response.requires_device_verification)) {
        console.log('[useAuth] Additional auth step required, returning response')
        // Don't set user or authenticated state for verification flow
        // Don't set error either - this is a normal flow requiring verification
        return response;
      }
      
      // Handle successful login with user data
      if (response.success && response.user) {
        console.log('[useAuth] Login successful, setting user and auth state')
        setUser(response.user);
        setIsAuthenticated(true);
        return response;
      } else {
        const errorMessage = response.error || response.message || 'Login failed';
        console.log('[useAuth] Login failed:', errorMessage)
        setError(errorMessage);
        return response;
      }
    } catch (err: any) {
      console.error('[useAuth] Login exception:', err)
      console.error('[useAuth] Error details:', {
        message: err?.message,
        response: err?.response,
        stack: err?.stack
      })
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      console.log('[useAuth] Login attempt finished')
      setIsLoading(false);
    }
  }, []);

  const signup = useCallback(async (data: SignupData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.signup(data);
      
      if (!response.success) {
        const errorMessage = response.error || response.message || 'Signup failed';
        setError(errorMessage);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetSendOTP = useCallback(async (contact: string, captchaToken?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.resetSendOTP(contact, captchaToken);
      
      if (!response.success) {
        const errorMessage = response.error || response.message || 'Failed to send reset code';
        setError(errorMessage);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendVerificationOTP = useCallback(async (data: {
    contact?: string;
    sessionToken?: string;
    purpose?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.sendVerificationOTP(data.contact, data.sessionToken);
      
      if (!response.success) {
        const errorMessage = response.error || response.message || 'Failed to send verification code';
        setError(errorMessage);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verify = useCallback(async (data: {
    contact?: string;
    otp: string;
    new_password?: string;
    session_token?: string;
    purpose?: string;
    userId?: number;
  }) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.verify(data);
      
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else if (!response.success) {
        const errorMessage = response.error || response.message || 'Verification failed';
        setError(errorMessage);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const oauthLogin = useCallback(async (provider: 'google' | 'microsoft', token: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.oauthLogin(provider, token);
      
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        return response;
      } else {
        const errorMessage = response.error || response.message || 'OAuth login failed';
        setError(errorMessage);
        return response;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initiateOAuthFlow = useCallback((provider: 'google' | 'microsoft' | 'intuit' | 'xero', redirectTo?: string, loginHint?: string, skipAccountSelection?: boolean) => {
    authService.initiateOAuthFlow(provider, redirectTo, loginHint, skipAccountSelection);
  }, []);

  const logout = useCallback(async (redirectTo?: string) => {
    try {
      setIsLoading(true);
      // Use signOut for manual user logout
      await authService.signOut(redirectTo);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getTokens = useCallback(() => {
    return authService.getTokens();
  }, []);

  // Check if current user is a root user with unrestricted access
  const isRoot = useMemo(() => user?.role === 'root', [user?.role]);

  // Derive client info from user's client_id
  const client = useMemo(() => {
    if (!user?.client_id) return null;
    const clientId = typeof user.client_id === 'string' ? parseInt(user.client_id, 10) : user.client_id;
    return {
      id: clientId,
      name: '' // Name is typically loaded separately if needed
    };
  }, [user?.client_id]);

  return {
    user,
    client,
    isAuthenticated,
    isLoading,
    error,
    isRoot,
    login,
    signup,
    resetSendOTP,
    sendVerificationOTP,
    verify,
    oauthLogin,
    initiateOAuthFlow,
    logout,
    clearError,
    getTokens
  };
}
